import { format } from 'date-fns'
import { useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Calendar } from '@/components/ui/calendar'
import { CalendarIcon } from '@/components/ui/icons'
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from '@/components/ui/popover'
import { formatDate, parseDate } from '@/dates'
import { cn } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { MMDataOverviewSearchParamsSchema } from '@/pages/luxe-submissions-mm-data-overview'
import { useTranslation } from '@/translations/i18n'
import type { MMDataOverviewDatePreset } from '../types'

const DATE_PRESETS: MMDataOverviewDatePreset[] = [
	'last_6_months',
	'last_3_months',
	'this_month'
]

function MMOverviewDateFilter() {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MMDataOverviewSearchParamsSchema
	)
	const activePreset =
		searchParams.dateFrom && searchParams.dateTo
			? undefined
			: (searchParams.preset ?? 'this_month')
	const [calendarRange, setCalendarRange] = useState<DateRange | undefined>(
		searchParams.dateFrom && searchParams.dateTo
			? {
					from: parseDate(searchParams.dateFrom),
					to: parseDate(searchParams.dateTo)
				}
			: undefined
	)
	const [isCalendarOpen, setIsCalendarOpen] = useState(false)

	const customDateLabel =
		searchParams.dateFrom && searchParams.dateTo && !activePreset
			? `${formatDate(searchParams.dateFrom, 'MMM d, yyyy')} - ${formatDate(searchParams.dateTo, 'MMM d, yyyy')}`
			: null

	const handleCalendarSelect = () => {
		if (calendarRange?.from && calendarRange?.to) {
			const dateFrom = format(calendarRange.from, 'yyyy-MM-dd')
			const dateTo = format(calendarRange.to, 'yyyy-MM-dd')

			setSearchParams({ preset: undefined, dateFrom, dateTo })
			setIsCalendarOpen(false)
		}
	}

	const handlePresetChange = (preset: MMDataOverviewDatePreset) => {
		setSearchParams({
			preset,
			dateFrom: undefined,
			dateTo: undefined
		})
	}

	return (
		<div className='flex items-center justify-between'>
			<div className='flex h-10 items-center gap-0 overflow-hidden rounded-lg border border-neutral-300'>
				{DATE_PRESETS.map((preset) => (
					<button
						className={cn(
							'h-full cursor-pointer border-r px-4 py-2 font-medium text-neutral-700 text-sm transition-colors last:border-none hover:bg-neutral-50',
							preset === activePreset && 'bg-neutral-100'
						)}
						key={preset}
						onClick={() => handlePresetChange(preset)}
						type='button'
					>
						{t(`luxe_submissions:${preset}`)}
					</button>
				))}
			</div>

			<Popover onOpenChange={setIsCalendarOpen} open={isCalendarOpen}>
				<PopoverTrigger
					className={cn(
						'flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-4 font-medium text-sm transition-colors hover:bg-neutral-100',
						customDateLabel
							? 'text-neutral-900'
							: 'text-neutral-500'
					)}
				>
					<CalendarIcon className='text-neutral-700' size={20} />
					{customDateLabel ?? t('luxe_submissions:select_dates')}
				</PopoverTrigger>
				<PopoverContent align='end' className='w-auto p-0'>
					<Calendar
						defaultMonth={calendarRange?.from}
						disabled={{ after: new Date() }}
						mode='range'
						numberOfMonths={2}
						onSelect={setCalendarRange}
						selected={calendarRange}
					/>

					<div className='flex justify-end border-neutral-300 border-t p-4'>
						<ButtonLegacy onClick={handleCalendarSelect} size='sm'>
							{t('common:apply')}
						</ButtonLegacy>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	)
}

export { MMOverviewDateFilter }
